<script lang="ts">

import dayjs from "dayjs";
import {asValue} from "$lib/utils/lenkradDate";
import {Button, Input, Label, Modal, Select} from "flowbite-svelte";
import type {Contract, ContractHistory, Employee} from "$lib/types/employee";
import {extendContract} from "$lib/calls/employee";
import {invalidateAll} from "$app/navigation";
import {onMount} from "svelte";
import {mailTemplates} from "$lib/stores/mailTemplates";
import {getEmailTemplates, sendEmailTemplate} from "$lib/calls/mail";
import type {EmailTemplate} from "$lib/types/EmailTemplate";
import {qualifications} from "$lib/stores/qualifications";
import generateContractPreview, {generateContractModification} from "$lib/utils/templating/generateContractPreview";
import {BuildingSolid, PenSolid} from "flowbite-svelte-icons";
import {formDataPost, retire} from "$lib/api";
import {findUnmatchedVariables} from "$lib/utils/templating/parseHandlebars";
import {allUnits} from "$lib/stores/unit";
import EmailTemplateEditor from "$lib/components/EmailTemplateEditor.svelte";
import ModalFooter from "$lib/components/ui/ModalFooter.svelte";
import ModalHeader from "$lib/components/ui/ModalHeader.svelte";

export let open: boolean;

export let currentContract: Contract;

export let employee: Employee;

let submitting = false;
let currentHistory: any;
let prolongingContract = {
    newEndDate: dayjs().add(6, 'months').format('YYYY-MM-DD'),
    contract: currentContract
}
let mailTemplate: EmailTemplate;
let preview: Blob;
let prolongedContract: Contract;
let mailData: ContractHistory;
const prolongContract = async () => {
    submitting = true;
    try{
        prolongedContract = await extendContract(employee.uuid, {
            contract: currentContract,
            newEndDate: currentHistory.endDate
        });
        mailData = await formDataPost('/document/contract', {
            file: preview,
            connectedEntity: 'EmployeeContract',
            entityId: prolongedContract.id,
            historyId: prolongedContract.history[prolongedContract.history.length - 1].id
        })
        // location.reload();
    } catch (e) {

    } finally {
        submitting = false;
    }

}
const generatePreview = async () => {
    submitting = true;
    preview = await generateContractModification(employee, currentContract, currentHistory, 'prolonging',{
        limited: !!currentHistory.endDate,
        employmentPartTimeFullTime: currentContract.contractType === 'fulltime' ? 'Vollzeit' : 'Teilzeit'
    })
    submitting = false;
}
const sendEmail = async () => {
    submitting = true
    try{
        const res = await sendEmailTemplate(mailTemplate.id, {
            fromEmail: 'legal@recrew.de',
            fromName: 'REcrew Legal',
            subject: 'REcrew_Vertragsverlängerung_zwischen_' + employee.name + ' und REcrew GmbH zur Signatur bereit!',
            entityId: employee.id,
            content: mailTemplate.content.replace(/<% body %>/g, ''),
            toEmail: employee.email,
            toName: employee.name,
            variables: findUnmatchedVariables(mailTemplate.content)
        })
        await invalidateAll()
    } catch (e) {

    } finally {
        submitting = false
        open = false;
    }

}
const abort = () => {
    if(mailData){
        retire(`/employee/${employee.uuid}/contract/${currentContract.id}/history/${mailData.contract.history[mailData.contract.history.length - 1].id}?silent=true`).then((emp) => {
            preview = null;
            submitting = false;
        })
    }
}
onMount(async () => {
    if($mailTemplates.length === 0){
        $mailTemplates = await getEmailTemplates();
    }
    if(currentContract){
        mailTemplate = $mailTemplates.find(template => template.level === 'active' && template.name.includes('Verlängerung_versendet'))
        currentHistory = {
            ...currentContract.currentHistory,
            historyType: 'prolonging',
            startDate: dayjs(currentContract.currentHistory.startDate.value).add(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().add(7, 'months').date(1).subtract(1, 'day').format('YYYY-MM-DD')
        };
        console.log({mailTemplate})
        // mailData = currentContract.currentHistory
    }
})
</script>
<Modal size="lg" bind:open title="Vertrag verlängen">
    <svelte:fragment slot="header">
        <ModalHeader>Vertrag verlängen</ModalHeader>
    </svelte:fragment>
    {#if currentHistory && currentContract.currentHistory && dayjs().isBefore(dayjs(asValue(currentContract.currentHistory.endDate)).add(33, 'day'))}
        {#if preview}
            <p>
                Vertrag bis <strong>{dayjs(asValue(currentHistory.endDate)).format('DD.MM.YYYY')}</strong> verlängern?
            </p>
        {:else}
            <p>
                Vertrag (momentan gültig bis <strong>{dayjs(asValue(currentContract.currentHistory.endDate)).format('DD.MM.YYYY')}</strong>) verlängern?
            </p>
            <Label class="space-y-2">
                <span>Verlängern bis</span>
                <Input min="{dayjs(asValue(currentContract.currentHistory.endDate)).add(1, 'day').format('YYYY-MM-DD')}" type="date" bind:value={currentHistory.endDate}/>
            </Label>
        {/if}
    {:else}
        Kann nicht verlängt werden.
    {/if}
    {#if mailData}
        {#if mailTemplate && $mailTemplates.filter(x => x.level === 'active').length !== 1}
            <Select required bind:value={mailTemplate.id} on:change={(e) => {const id = mailTemplate.id; mailTemplate = null; setTimeout(() => {mailTemplate = $mailTemplates.find(x => x.id === id)})}} items={$mailTemplates.filter(x => x.level === 'active').map(x => ({...x, value: x.id}))}/>
        {/if}
        {#if mailTemplate}
            <div class="mt-3">
                <EmailTemplateEditor on:update={(e) => {mailTemplate = e.detail}} preview={true} template={mailTemplate} fillData={{Employee: employee, Contract: mailData.contract.history[mailData.contract.history.length - 1], Unit: $allUnits.find(x => x.id === (employee?.unitId || 1)), Input: {}}}/>
            </div>

        {/if}
    {/if}
    <svelte:fragment slot="footer">
        <ModalFooter disabled={submitting} showEdit={!!preview} on:abort={() => {abort(); open = false}} on:edit={abort}>
            <svelte:fragment slot="right">
                {#if preview}
                    <Button class="!text-decoration-none whitespace-nowrap" download="{employee.name.replaceAll(' ','_')}.docx" href={URL.createObjectURL(preview)} target="_blank" color="light">Vorschau erzeugter Vertrag</Button>
                    {#if mailData}
                        <Button disabled={submitting} class="bg-sky-100 text-neutral-600 whitespace-nowrap" color="light" on:click={() => sendEmail()}>E-Mail versenden</Button>
                    {:else}
                        <Button disabled={submitting} class="bg-sky-100 text-neutral-600 whitespace-nowrap" color="light" on:click={() => prolongContract()}>Vertrag verlängern</Button>
                    {/if}
                {:else}
                    <Button disabled={submitting} class="bg-sky-100 text-neutral-700 whitespace-nowrap" on:click={() => generatePreview()}><BuildingSolid class="w-4 mr-2"/>Vorschau erstellen</Button>
                {/if}
            </svelte:fragment>
        </ModalFooter>

    </svelte:fragment>
</Modal>